import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { OtpInput } from '@/components/OtpInput';
import { authApi, otpErrorKey } from '@/lib/api/auth';
import { ApiError } from '@/lib/api-client';
import { useAuthActions } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';

const RESEND_LOCK_SECONDS = 30;

interface NavState {
  phone?: string;
  email?: string;
  /** Non-prod Iran admin bypass — auto-fill + auto-submit when present. */
  debugCode?: string;
}

export function VerifyOtp(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as NavState;
  const destination = state.phone ?? state.email;
  const { signInWithToken } = useAuthActions();

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(RESEND_LOCK_SECONDS);

  // No state? User landed here directly — bounce back to welcome.
  useEffect(() => {
    if (!state.phone && !state.email) {
      navigate('/', { replace: true });
    }
  }, [state.phone, state.email, navigate]);

  // Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  const verify = useCallback(
    async (digits: string) => {
      if (digits.length !== 6) return;
      setBusy(true);
      setError(null);
      try {
        const { customToken } = await authApi.verifyOtp({
          phone: state.phone,
          email: state.email,
          code: digits,
        });
        await signInWithToken(customToken);
        navigate('/', { replace: true });
      } catch (err) {
        console.error(err);
        if (err instanceof ApiError && err.status === 401) {
          const body = err.body as { message?: string } | null;
          const msg = body?.message?.toLowerCase() ?? '';
          if (msg.includes('expire')) setError(t('auth.errors.expired'));
          else if (msg.includes('too many')) setError(t('auth.errors.lockout'));
          else setError(t('auth.errors.invalidCode'));
        } else {
          // 429/409/503 each map to a distinct message (throttle /
          // account-provisioning conflict / channel unavailable) — the
          // catch-all 'unexpected' only covers genuinely unknown errors.
          setError(t(otpErrorKey(err, 'auth.errors.unexpected')));
        }
      } finally {
        setBusy(false);
      }
    },
    [state.phone, state.email, signInWithToken, navigate, t],
  );

  // WebOTP (mvp-v1 §4.3 — required on web): when the SMS arrives with a
  // properly formatted origin-bound code, Chromium surfaces it through
  // `navigator.credentials.get({otp})`. We fill the boxes and submit —
  // zero manual code effort. Feature-detected; Safari/Firefox fall back
  // to manual entry. The AbortController cancels the pending request on
  // unmount so a late SMS can't resolve into a dead component.
  useEffect(() => {
    if (typeof window === 'undefined' || !('OTPCredential' in window)) return;
    const ac = new AbortController();
    void navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal } as CredentialRequestOptions)
      .then((cred) => {
        const otp = (cred as { code?: string } | null)?.code;
        if (otp && /^\d{6}$/.test(otp)) {
          setCode(otp);
          void verify(otp);
        }
      })
      .catch(() => {
        /* aborted (unmount) or dismissed — manual entry continues */
      });
    return () => ac.abort();
  }, [verify]);

  // Iran-bypass autofill: when Welcome handed us a `debugCode`, drop
  // it into the boxes and submit on the next tick. We defer the
  // setCode + verify into the timeout so React's effect linter doesn't
  // see a setState in the synchronous effect body.
  useEffect(() => {
    if (!state.debugCode || !/^\d{6}$/.test(state.debugCode)) return;
    const code = state.debugCode;
    const id = window.setTimeout(() => {
      setCode(code);
      void verify(code);
    }, 50);
    return () => window.clearTimeout(id);
  }, [state.debugCode, verify]);

  const resend = useCallback(async () => {
    if (resendIn > 0 || busy) return;
    setError(null);
    setBusy(true);
    try {
      const res = await authApi.sendOtp({ phone: state.phone, email: state.email });
      setResendIn(RESEND_LOCK_SECONDS);
      if (res.debugCode && /^\d{6}$/.test(res.debugCode)) {
        setCode(res.debugCode);
        const code = res.debugCode;
        window.setTimeout(() => void verify(code), 50);
      }
    } catch (err) {
      setError(t(otpErrorKey(err, 'auth.errors.sendFailed')));
    } finally {
      setBusy(false);
    }
  }, [busy, resendIn, state.phone, state.email, t, verify]);

  if (!destination) {
    return <></>; // useEffect navigates away; render nothing in the interim
  }

  return (
    <main className="relative mx-auto flex min-h-dvh max-w-sm flex-col px-6 pb-10 pt-6">
      {/* Back button */}
      <header className="-ms-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-10 items-center gap-1 rounded-md px-2 text-sm text-muted hover:text-foreground"
          aria-label={t('common.back')}
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          {t('common.back')}
        </button>
      </header>

      <section className="mt-8 flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-foreground">{t('otp.title')}</h1>
        <p className="break-words text-sm text-muted">{t('otp.subtitle', { destination })}</p>
      </section>

      <section className="mt-8 flex flex-col gap-4">
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={verify}
          disabled={busy}
          error={error ?? undefined}
        />

        <button
          type="button"
          disabled={busy || code.length !== 6}
          onClick={() => void verify(code)}
          className={cn(
            'h-12.5 w-full rounded-button bg-primary font-semibold text-primary-foreground',
            'transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {busy ? t('common.loading') : t('otp.verify')}
        </button>

        <div className="flex flex-col items-center gap-2 pt-2">
          {resendIn > 0 ? (
            <p className="text-sm text-muted">{t('otp.resendIn', { seconds: resendIn })}</p>
          ) : (
            <button
              type="button"
              onClick={() => void resend()}
              disabled={busy}
              className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
            >
              {t('otp.resend')}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="text-sm text-muted hover:text-foreground"
          >
            {t('otp.changeIdentity')}
          </button>
        </div>
      </section>
    </main>
  );
}
